import type { ProperNoun } from "@/lib/models/properNoun";

/**
 * 基本校正の固有名詞除外 UI 状態から、rule-based API の options を組み立てる。
 *
 * サーバ側では誤記系・注意語・同じ語の繰り返しの個別指摘に加え、
 * 漢字の開き揺れ・送り仮名の揺れ・動詞の漢字／かな揺れ
 * （`kanji_hiraki_variation` / `okurigana_variation` / `verb_variation`）の出現も登録表記から除外する。
 *
 * - マスタ OFF → 登録済みマージも明示指定もなし
 * - マスタ ON かつ全件（ids=null または全 ID）→ `useRegisteredProperNouns: true`
 * - マスタ ON かつ選択 0 件 → トグル ON なのに除外ゼロは誤操作になりやすいので全件扱い
 * - マスタ ON かつ一部 → 選択名称のみ明示指定（登録済み自動マージはオフ）
 * - マスタ ON かつ一部だが名称が解決できない（一覧未ロード等）→ fail-safe で全件扱い
 */
export function buildProperNounExclusionOptions(params: {
  enabled: boolean;
  /** null = 登録済みすべてを対象 */
  selectedProperNounIds: string[] | null;
  properNouns: ReadonlyArray<Pick<ProperNoun, "properNounId" | "name">>;
}): {
  useRegisteredProperNouns: boolean;
  properNouns?: string[];
} {
  if (!params.enabled) {
    return { useRegisteredProperNouns: false };
  }

  const allIds = params.properNouns.map((p) => p.properNounId);
  const selectedIds = params.selectedProperNounIds;

  // トグル ON + 選択 0 件は「除外しない」ではなく「全件」とみなす
  if (selectedIds != null && selectedIds.length === 0) {
    return { useRegisteredProperNouns: true };
  }

  const isAllSelected =
    selectedIds == null
    || (allIds.length > 0
      && allIds.every((id) => selectedIds.includes(id))
      && selectedIds.length >= allIds.length)
    || (allIds.length === 0 && selectedIds == null);

  if (isAllSelected) {
    return { useRegisteredProperNouns: true };
  }

  const selectedIdSet = new Set(selectedIds);
  const names = params.properNouns
    .filter((p) => selectedIdSet.has(p.properNounId))
    .map((p) => p.name.trim())
    .filter((name) => name.length > 0);

  // 個別選択なのに名称が 0 件 = ストア未ロード等。除外が黙って無効化するのを防ぐ
  if (names.length === 0) {
    return { useRegisteredProperNouns: true };
  }

  return {
    useRegisteredProperNouns: false,
    properNouns: names,
  };
}
